// -------------------------------------------------
// OBJECTS AND DATA PERSISTED IN MEMORY
// -------------------------------------------------

import { jwtDecode } from "jwt-decode";
import { getNewAccessToken } from "./backend/auth";

let authFunctions: {
  signout?: () => void;
} = {};

type AuthDataType = {
  accessToken?: string | null;
  refreshToken?: string | null;
};

// Initialize authData before using it
const authData: AuthDataType = {};

// -------------------------------------------------
// SETTERS
// -------------------------------------------------

export const setAuthFunctions = (functions: typeof authFunctions) => {
  authFunctions = { ...authFunctions, ...functions };
};

export const setAuthData = ({ accessToken, refreshToken }: AuthDataType) => {
  if (refreshToken !== undefined) {
    authData.refreshToken = refreshToken;
  }
  if (accessToken !== undefined) {
    authData.accessToken = accessToken;
  }

  // Store refresh token in local storage and in the service file
  if (refreshToken) {
    localStorage.setItem("on-demand-refresh-token", refreshToken);
  } else {
    localStorage.removeItem("on-demand-refresh-token");
  }
};

export const updateToken = async () => {
  try {
    // eslint-disable-next-line prefer-const
    let { accessToken, refreshToken } = getAuthData();
    const response = await getNewAccessToken({ refreshToken });
    if (!response) throw new Error("Failed to get new access token.");
    const { accessToken: newAccessToken } = response;
    accessToken = newAccessToken;

    // Set the new access token in the store
    if (!accessToken) throw new Error("Failed to receive access token.");
    setAuthData({
      accessToken: newAccessToken,
      refreshToken,
    });
    return accessToken;
  } catch (err) {
    console.log(err);
    throw new Error("Failed to update token");
  }
};

// -------------------------------------------------
// UTILITY METHODS
// -------------------------------------------------

export const getAuthData = () => {
  return authData;
};

export const logout = () => {
  if (!authFunctions.signout) {
    console.error("Signout function is not set yet.");
    return;
  }
  authFunctions.signout();
};

export const isTokenExpired = (token: string) => {
  let decodedToken = null;

  try {
    decodedToken = jwtDecode(token);

    if (!decodedToken) throw new Error();
    const now = Math.round(new Date().getTime() / 1000);
    const { exp } = decodedToken;

    if (exp && now > exp) {
      return true;
    }
    return false;
  } catch (error) {
    console.log(error);
    return true;
  }
};
